import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import flattenDeep from 'lodash/flattenDeep';
import values from 'lodash/values';
import isNull from 'lodash/isNull';
import first from 'lodash/first';
import get from 'lodash/get';
import round from 'lodash/round';
import { DateTime } from 'luxon';
import Bugsnag from '@bugsnag/js';

import { api } from 'api';
import { combineActions } from 'utils/combineActions';
import { isListedSubscription, isTrialSubscription } from 'utils/subscriptions';

const initialState = {
  accessToken: null,
  user: {},
  plans: [],
  promotion: null,
  isCodeSet: null,
  isPromoValid: null,
  language: null,
  settings: null,
  hasError: false,
  errors: [],
  i18nKey: null,
  responseMessage: null,
};

const actions = {
  login: createAsyncThunk(
    'account/login',
    async (credentials, { rejectWithValue }) => {
      try {
        const response = await api.post('user/auth', credentials);
        localStorage.setItem('accessToken', response.token);
        return response;
      } catch (err) {
        const errorData = err.data || {
          message: 'Login failed: Server error',
          errors: ['Login failed: Server error']
        };

        // Only log to Bugsnag if it's not a 422 validation error (incorrect credentials)
        if (typeof Bugsnag !== 'undefined' && err.status !== 422) {
          Bugsnag.notify(new Error('Login Error'), function(event) {
            event.severity = 'error';
            event.addMetadata('loginError', {
              credentials: { email: credentials.email },
              errorData
            });
          });
        }

        return rejectWithValue(errorData);
      }
    },
  ),
  adminLogin: createAsyncThunk(
    'account/adminLogin',
    async (credentials, { rejectWithValue }) => {
      try {
        const response = await api.post('user/auth/admin', credentials);
        localStorage.setItem('accessToken', response.token);
        return response;
      } catch (err) {
        return rejectWithValue(err.data);
      }
    }
  ),
  register: createAsyncThunk(
    'account/register',
    async (credentials, { rejectWithValue }) => {
      try {
        const response = await api.post('user/register', credentials);
        localStorage.setItem('accessToken', response.token);
        return response;
      } catch (err) {
        return rejectWithValue(err.data);
      }
    }
  ),
  changePassword: createAsyncThunk(
    'account/changePassword',
    async (data, { rejectWithValue }) => {
      try {
        await api.post('user/changePassword', data)
      } catch (err) {
        return rejectWithValue(err.data);
      }
    },
  ),
  forgotPassword: createAsyncThunk(
    'account/forgotPassword',
    async (credentials, { rejectWithValue }) => {
      try {
        return await api.post('user/forgotPassword', credentials);
      } catch (err) {
        return rejectWithValue(err.data);
      }
    }
  ),
  fetchSettings: createAsyncThunk(
    'account/fetchSettings',
    (_, { getState }) => {
      const token = getState().user.accessToken;
      return api.get('settings').then(data => ({ data, token }));
    },
  ),
  applyPromoCode: createAsyncThunk(
    'account/applyPromoCode',
    async (code, { rejectWithValue }) => {
      try {
        return await api.post('user/membership/promo', { promo_code: code });
      } catch (err) {
        return rejectWithValue(err.data);
      }
    },
  ),
  fetchUser: createAsyncThunk(
    'account/fetchUser',
    async () => {
      const response = await api.get('/user');
      localStorage.setItem('user', response);
      return response;
    }
  ),
  updateUser: createAsyncThunk(
    'account/updateUser',
    async (data, { rejectWithValue }) => {
      try {
        return await api.post('user', data, { unwrap: true });
      } catch (err) {
        return rejectWithValue(err.data);
      }
    },
  ),
  getSignature: createAsyncThunk(
    'account/getSignature',
    planIDs => api.post('user/membership/signature', {
      plan_ids: planIDs,
    })
  ),
  createCoinRemitterInvoice: createAsyncThunk(
    'account/createCoinRemitterInvoice',
    ({ planIDs, coin }) => api.post('user/membership/coinremitter/create', {
      plan_ids: planIDs,
      coin,
    })
  ),
  createAnyPayPayment: createAsyncThunk(
    'account/createAnyPayPayment',
    planIDs => api.post('user/membership/anypay/pay', {
      plan_ids: planIDs,
    })
  ),
  fetchPlategaMethods: createAsyncThunk(
    'account/fetchPlategaMethods',
    () => api.get('user/membership/platega/methods')
  ),
  createPlategaPayment: createAsyncThunk(
    'account/createPlategaPayment',
    ({ planIDs, method }) => api.post('user/membership/platega/create', {
      plan_ids: planIDs,
      method,
      return_url: `${window.location.origin}/app/platega/success`,
      fail_url: `${window.location.origin}/app/platega/error`,
    })
  ),
  createPayPalOrder: createAsyncThunk(
    'account/createPayPalOrder',
    async ({ planIDs, isDemo }) => {
      const response = await api.post('user/membership/paypal/create-order', {
        planIDs, isDemo,
      });
      return response.orderId;
    }
  ),
  savePayPalTransaction: createAsyncThunk(
    'account/savePayPalTransaction',
    async ({ orderId, planIDs, isDemo }) => {
      const response = await api.post('user/membership/paypal/check', {
        orderId, planIDs, isDemo
      });
      localStorage.setItem('user', response);
      return response;
    }
  ),
  fetchPlans: createAsyncThunk(
    'account/fetchPlans',
    () => api.get('payment/plans'),
  ),
  fetchPromotion: createAsyncThunk(
    'account/fetchPromotion',
    (promotionId) => api.get(`promotions/${promotionId}`),
  ),
  setTVCode: createAsyncThunk(
    'account/setTVCode',
    code => api.post('user/saveTVCode', code),
  ),
  deleteAccount: createAsyncThunk(
    'account/deleteAccount',
    () => api.delete('user/delete'),
  ),
  sendFailLog: createAsyncThunk(
    'account/sendFailLog',
    ({ data, userId, name, type }) => api.post('logs/fail', {
      name,
      failure_type: type,
      description: data,
      user_id: userId,
    })
  ),
};

const slice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    setAccessToken: (state, { payload }) => {
      localStorage.setItem('accessToken', payload);
      state.accessToken = payload;
    },
    setLanguage: (state, { payload }) => {
      localStorage.setItem('language', payload);
      state.language = payload;
    },
    setUser: (state, { payload }) => {
      state.user = payload;
    },
    setError: (state, { payload }) => {
      state.errors = [payload] ;
      state.hasError = true;
    },
    logout: state => {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      localStorage.removeItem('devices');
    },
    clearNotifications: state => {
      state.isCodeSet = initialState.isCodeSet;
      state.isPromoValid = initialState.isPromoValid;
    },
    clearErrors: state => {
      state.hasError = false;
      state.errors = initialState.errors;
      state.i18nKey = initialState.i18nKey;
    },

    clear: () => initialState,
  },
  extraReducers: builder =>
    builder
      .addCase(actions.fetchSettings.fulfilled, (state, { payload }) => {
        if (payload.token === state.accessToken) {
          state.settings = payload.data;
        }
      })
      .addCase(actions.fetchUser.fulfilled,
        (state, { payload }) => {
          state.user = payload;
          state.user.credits = round(payload.credits / 100, 2);
        })
      .addCase(actions.updateUser.fulfilled,
        (state, { payload: { data, i18n_key } }) => {
          state.user.name = data.name;
          state.user.email = data.email;
          state.i18nKey = i18n_key;
          state.hasError = false;
        })
      .addCase(actions.savePayPalTransaction.fulfilled,
        (state, { payload }) => {
          state.user.credits = round(payload.credits / 100, 2);
          state.user.subscription_expires_at = payload.subscription_expires_at;
        })
      .addCase(actions.setTVCode.fulfilled,
        (state => { state.isCodeSet = true })
      )
      .addCase(actions.setTVCode.rejected,
        (state => { state.isCodeSet = false })
      )
      .addCase(actions.applyPromoCode.fulfilled,
        (state, { payload }) => {
          state.user.credits = round(payload.credits / 100, 2);
          state.user.subscription_expires_at = payload.subscription_expires_at;
          state.isPromoValid = true;
        })
      .addCase(actions.applyPromoCode.rejected,
        (state, { payload }) => {
          state.isPromoValid = false;
          state.i18nKey = get(payload, 'i18n_key', null);
        })
      .addCase(actions.fetchPlans.fulfilled,
        (state, { payload }) => {
          state.plans = payload;
        })
      .addCase(actions.fetchPromotion.fulfilled,
        (state, { payload }) => {
          state.promotion = payload;
        })
      .addCase(actions.forgotPassword.fulfilled,
        (state, { payload }) => {
          state.responseMessage = payload.message;
        })
      .addCase(actions.forgotPassword.rejected,
        (state, { payload }) => {
          state.hasError = true;
          state.errors = [payload.message];
        })
      .addMatcher(
        combineActions(
          actions.login.fulfilled,
          actions.register.fulfilled,
          actions.adminLogin.fulfilled
        ),
        (state, { payload }) => {
          state.accessToken = payload.token;
          state.user = payload.user;
          state.settings = null;
        })
      .addMatcher(
        combineActions(
          actions.login.pending,
          actions.register.pending,
          actions.forgotPassword.pending,
          actions.updateUser.pending,
          actions.login.fulfilled,
          actions.register.fulfilled,
          actions.changePassword.fulfilled,
        ),
        (state) => {
          state.hasError = false;
          state.i18nKey = null;
        }
      )
      .addMatcher(
        combineActions(
          actions.login.rejected,
          actions.register.rejected,
          actions.changePassword.rejected,
          actions.updateUser.rejected
        ),
        (state, { payload }) => {
          state.hasError = true;
          state.i18nKey = payload?.i18n_key;

          if (payload?.errors && Array.isArray(flattenDeep(values(payload.errors)))) {
            state.errors = flattenDeep(values(payload.errors));
          } else if (payload?.message) {
            state.errors = [payload.message];
          } else {
            state.errors = ['An unexpected error occurred. Please try again.'];
          }
        })
});

export const account = {
  reducer: slice.reducer,
  actions: {
    ...slice.actions,
    ...actions,
  },
  selectors: {
    accessToken: createSelector(
      state => state.user.accessToken,
      accessToken => accessToken,
    ),
    isAuthenticated: createSelector(
      state => state.user.accessToken,
      accessToken => !!accessToken,
    ),
    language: createSelector(
      state => state.user.language,
      language => language,
    ),
    user: createSelector(
      state => state.user.user,
      user => user,
    ),
    isAdmin: createSelector(
      state => state.user.user,
      user => user && user.user_role > 1,
    ),
    isActiveUser: createSelector(
      state => state.user.user,
      user => user && user.credits > 0,
    ),
    credits: createSelector(
      state => state.user.user,
      user => user && user.credits,
    ),
    // True while the only credits on the account came from the sign-up trial, so "free" is
    // never said to someone who has paid.
    isOnFreeTrial: createSelector(
      state => state.user.user,
      (user) => {
        const subscriptions = get(user, 'subscriptions', []).filter(isListedSubscription);
        return user?.credits > 0 && subscriptions.length > 0 && subscriptions.every(isTrialSubscription);
      },
    ),
    subscriptionExpiresAt: createSelector(
      state => state.user.user,
      user => user && DateTime.fromMillis(user.subscription_expires_at * 1000).toLocaleString(),
    ),
    // How many days the credits last at the current daily cost.
    subscriptionDays: createSelector(
      state => state.user.user,
      (user) => {
        const dailyCost = get(user, 'device_subscriptions.daily_cost', 1.0);
        return dailyCost > 0 ? Math.floor((Number(user?.credits) || 0) / dailyCost) : 0;
      },
    ),
    displayName: createSelector(
      state => state.user.user,
      user => user?.name || user?.email?.split('@')[0] || '',
    ),
    settings: createSelector(
      state => state.user.settings,
      settings => settings,
    ),
    features: createSelector(
      state => state.user.settings,
      settings => settings && settings.features,
    ),
    plans: createSelector(
      state => state.user.plans,
      plans => plans,
    ),
    promotion: createSelector(
      state => state.user.promotion,
      promotion => promotion,
    ),
    promotionId: createSelector(
      state => state.user.settings,
      settings => settings?.promotion_id || null,
    ),
    hasError: createSelector(
      state => state.user.hasError,
      hasError => hasError,
    ),
    errors: createSelector(
      state => state.user.errors,
      errors => errors,
    ),
    firstError: createSelector(
      state => state.user.errors,
      errors => first(errors),
    ),
    i18nKey: createSelector(
      state => state.user.i18nKey,
      i18nKey => i18nKey,
    ),
    responseMessage: createSelector(
      state => state.user.responseMessage,
      responseMessage => responseMessage,
    ),
    isPromoValid: createSelector(
      state => state.user.isPromoValid,
      isPromoValid => isPromoValid,
    ),
    hasPromo: createSelector(
      state => state.user.isPromoValid,
      isPromoValid => !isNull(isPromoValid),
    ),
    isCodeSet: createSelector(
      state => state.user.isCodeSet,
      isCodeSet => isCodeSet,
    ),
    isPremiumUser: createSelector(
      state => state.user.user,
      user => get(user, 'device_subscriptions.active_subscriptions', []).length > 0,
    ),
    dailyCost: createSelector(
      state => state.user.user,
      user => get(user, 'device_subscriptions.daily_cost', 1.0),
    ),
  }
};
